import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/solid";
import { ActionIcon, Alert, Box, Button, Collapse, Divider, Group, Paper, ScrollArea, SegmentedControl, Select, Stack, Text, Title } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { saveAs } from "file-saver";
import React, { useMemo, useState } from "react";
import { FoundCountBadge } from "../../../components/common/FoundCountBadge";
import { useCertificatesDictionary } from "../../../hooks/queries/useCertificatesDictionary";
import { useCertificationFamiliesDictionary } from "../../../hooks/queries/useCertificationFamiliesDictionary";
import { dateInputStyles, getDateInputProps } from "../../../styles/dateInputStyles";
import { toLocalDateString } from "../../../utils/dateFormat";
import { ReportActionsToolbar } from "../components/ReportActionsToolbar";
import { ReportCriteriaBuilder, ReportCriteriaChip } from "../components/ReportCriteriaBuilder";
import { ReportCvExportModal } from "../components/ReportCvExportModal";
import { ReportEmptyState } from "../components/ReportEmptyState";
import { ReportResultsTable } from "../components/ReportResultsTable";
import { useCertificateReportSearch } from "../hooks/useCertificateReportSearch";
import { useReportSelection } from "../hooks/useReportSelection";
import { CertificateReportMatchMode, CertificateReportMatchModeValue, CertificateReportSearchRequest } from "../models/certificateReport.model";
import { certificateReportService } from "../services/certificateReportService";
import { DateRangePreset, getPresetRange, toObtainedDateStrings } from "../utils/dateRangePresets";

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = ["10", "20", "50", "100"];

export const CertificatesReport: React.FC = () => {
  const { data: certificates = [] } = useCertificatesDictionary();
  const { data: families = [] } = useCertificationFamiliesDictionary();

  const [chips, setChips] = useState<ReportCriteriaChip[]>([]);
  const [matchMode, setMatchMode] = useState<CertificateReportMatchModeValue>(CertificateReportMatchMode.Any);
  const [datePreset, setDatePreset] = useState<DateRangePreset>("any");
  const [customFrom, setCustomFrom] = useState<Date | null>(null);
  const [customTo, setCustomTo] = useState<Date | null>(null);

  const [certPickerValue, setCertPickerValue] = useState<string | null>(null);
  const [familyPickerValue, setFamilyPickerValue] = useState<string | null>(null);
  const [pendingFamily, setPendingFamily] = useState<{ id: number; name: string; members: { id: number; title: string }[] } | null>(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [submittedRequest, setSubmittedRequest] = useState<CertificateReportSearchRequest | null>(null);
  // Bumped on every explicit "Run Report" click so an unchanged filter still forces a real
  // refetch instead of silently reusing the cached result — see useCertificateReportSearch's doc comment.
  const [runId, setRunId] = useState(0);
  // Only Excel/CSV export runs synchronously here — "Export CV for selected/all" just opens
  // CvExportOptionsModal, which tracks its own loading state for the actual transfer.
  const [isExporting, setIsExporting] = useState(false);
  const [exportCvMode, setExportCvMode] = useState<"selected" | "all" | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(true);

  const selection = useReportSelection();
  const { data, isFetching } = useCertificateReportSearch(submittedRequest, runId);

  const certificateOptions = useMemo(
    () => certificates.map((c) => ({ value: String(c.id), label: c.title })),
    [certificates]
  );
  const familyOptions = useMemo(
    () => families.map((f) => ({ value: String(f.id), label: f.name })),
    [families]
  );

  const addChip = (chip: ReportCriteriaChip) => {
    setChips((prev) => (prev.some((c) => c.id === chip.id) ? prev : [...prev, chip]));
  };

  const handleAddCertificate = (value: string | null) => {
    if (!value) {
      return;
    }
    const certificate = certificates.find((c) => c.id === Number(value));
    if (certificate) {
      addChip({ id: certificate.id, label: certificate.title });
    }
    setCertPickerValue(null);
  };

  const handleSelectFamily = (value: string | null) => {
    if (!value) {
      return;
    }
    const family = families.find((f) => f.id === Number(value));
    if (family) {
      setPendingFamily({ id: family.id, name: family.name, members: family.certifications });
    }
    setFamilyPickerValue(null);
  };

  const handleConfirmFamily = () => {
    if (pendingFamily) {
      pendingFamily.members.forEach((m) => addChip({ id: m.id, label: m.title }));
    }
    setPendingFamily(null);
  };

  const handleRemoveChip = (id: number) => {
    setChips((prev) => prev.filter((c) => c.id !== id));
  };

  const buildFilter = () => {
    const obtainedDates: { obtainedFrom?: string; obtainedTo?: string } = {};
    if (datePreset !== "any") {
      if (datePreset === "custom") {
        if (customFrom) {
          obtainedDates.obtainedFrom = toLocalDateString(customFrom);
        }
        if (customTo) {
          obtainedDates.obtainedTo = toLocalDateString(customTo);
        }
      } else {
        Object.assign(obtainedDates, toObtainedDateStrings(getPresetRange(datePreset)));
      }
    }

    return {
      certificateIds: chips.map((c) => c.id),
      matchMode,
      ...obtainedDates,
    };
  };

  const handleRunReport = () => {
    setPage(1);
    selection.handleClearAll();
    setSubmittedRequest({ ...buildFilter(), page: 1, pageSize });
    setRunId((id) => id + 1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setSubmittedRequest((prev) => (prev ? { ...prev, page: newPage } : prev));
  };

  const handlePageSizeChange = (value: string | null) => {
    if (!value) {
      return;
    }
    const newPageSize = Number(value);
    setPageSize(newPageSize);
    setPage(1);
    // If a report is already showing, re-run immediately with the new page size — no need to
    // make the user click "Run Report" again just to change how many rows they see per page.
    setSubmittedRequest((prev) => (prev ? { ...prev, page: 1, pageSize: newPageSize } : prev));
  };

  const handleClearAllChips = () => {
    setChips([]);
  };

  const handleExportCvForSelected = () => setExportCvMode("selected");
  const handleExportCvForAll = () => setExportCvMode("all");

  const handleSelectedIdsChangedByReview = (ids: number[]) => {
    const idSet = new Set(ids);
    const toRemove = Array.from(selection.selectedIds).filter((id) => !idSet.has(id));
    const toAdd = ids.filter((id) => !selection.selectedIds.has(id));
    if (toRemove.length > 0) {
      selection.handleDeselectAllOnPage(toRemove);
    }
    if (toAdd.length > 0) {
      selection.handleSelectAllOnPage(toAdd);
    }
  };

  const downloadReportFile = async (format: "xlsx" | "csv") => {
    setIsExporting(true);
    try {
      const blob = await certificateReportService.exportFile(buildFilter(), format);
      const timestamp = new Date().toISOString().slice(0, 10);
      saveAs(blob, `CertificateReport_${timestamp}.${format}`);
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = () => downloadReportFile("xlsx");
  const handleExportCsv = () => downloadReportFile("csv");

  const rows = (data?.items ?? []).map((row) => ({
    personId: row.personId,
    firstName: row.firstName,
    lastName: row.lastName,
    email: row.email,
    thumbnailBase64: row.thumbnailBase64,
    matches: row.matchedCertificates.map((c) => ({ label: c.certificateName, date: c.obtainedDate })),
  }));
  const pagePersonIds = rows.map((r) => r.personId);

  return (
    <Stack gap="xl" pb="xl">
      <Box>
        <Title order={2}>By Certificates</Title>
        <Text c="dimmed" size="sm" mt={4}>
          Find people who hold specific certificates, or any certificate from a family (e.g. AWS, Azure), obtained in a given period.
        </Text>
      </Box>

      <Paper withBorder shadow="sm" radius="md" p="lg">
        <Stack gap="md">
          <Group justify="space-between" align="center" onClick={() => setIsFilterOpen((v) => !v)} style={{ cursor: "pointer" }}>
            <Text fw={600} size="sm" tt="uppercase" c="dimmed">
              Filter criteria
            </Text>
            <ActionIcon variant="subtle" color="gray" component="span" aria-label={isFilterOpen ? "Collapse filter" : "Expand filter"}>
              {isFilterOpen
                ? <ChevronUpIcon style={{ width: 16, height: 16 }} />
                : <ChevronDownIcon style={{ width: 16, height: 16 }} />}
            </ActionIcon>
          </Group>

          <Collapse in={isFilterOpen}>
            <Stack gap="md">

              <Select
                label="Certificate"
                placeholder="Search certificates..."
                searchable
                clearable
                data={certificateOptions}
                value={certPickerValue}
                onChange={handleAddCertificate}
                w="100%"
              />
              <Select
                label="Certificate Family"
                placeholder="Search families (e.g. AWS, Azure)..."
                searchable
                clearable
                data={familyOptions}
                value={familyPickerValue}
                onChange={handleSelectFamily}
                w="100%"
              />

              {pendingFamily && (
                <Alert color="brand" title={`${pendingFamily.name} — ${pendingFamily.members.length} certificate(s)`}>
                  <ScrollArea.Autosize mah={180} mb="xs">
                    <Stack gap={4}>
                      {pendingFamily.members.map((m) => (
                        <Text key={m.id} size="sm">
                          {m.title}
                        </Text>
                      ))}
                    </Stack>
                  </ScrollArea.Autosize>
                  <Group gap="xs">
                    <Button size="xs" color="brand" onClick={handleConfirmFamily}>
                      Add all
                    </Button>
                    <Button size="xs" variant="subtle" color="gray" onClick={() => setPendingFamily(null)}>
                      Cancel
                    </Button>
                  </Group>
                </Alert>
              )}

              {chips.length > 0 && (
                <>
                  <Divider />
                  <ReportCriteriaBuilder chips={chips} onRemove={handleRemoveChip} onClearAll={handleClearAllChips} />
                </>
              )}

              <Divider />

              <Group align="center" gap="xl" wrap="wrap">
                <Group gap="xs" align="center">
                  <Text size="sm" fw={500} c="dimmed">
                    Match:
                  </Text>
                  <SegmentedControl
                    size="xs"
                    value={String(matchMode)}
                    onChange={(v) => setMatchMode(Number(v) as CertificateReportMatchModeValue)}
                    data={[
                      { label: "Any of these", value: String(CertificateReportMatchMode.Any) },
                      { label: "All of these", value: String(CertificateReportMatchMode.All) },
                    ]}
                  />
                </Group>

                <Group gap="xs" align="center">
                  <Text size="sm" fw={500} c="dimmed">
                    Obtained:
                  </Text>
                  <SegmentedControl
                    size="xs"
                    value={datePreset}
                    onChange={(v) => setDatePreset(v as DateRangePreset)}
                    data={[
                      { label: "Any time", value: "any" },
                      { label: "This month", value: "thisMonth" },
                      { label: "Last month", value: "lastMonth" },
                      { label: "Custom", value: "custom" },
                    ]}
                  />
                </Group>
              </Group>

              {datePreset === "custom" && (
                <Stack gap="xs">
                  <DateInput
                    label="From"
                    value={customFrom}
                    onChange={setCustomFrom}
                    styles={dateInputStyles}
                    {...getDateInputProps()}
                  />
                  <DateInput
                    label="To"
                    value={customTo}
                    onChange={setCustomTo}
                    styles={dateInputStyles}
                    {...getDateInputProps()}
                  />
                </Stack>
              )}

              <Group justify="flex-end" mt="xs">
                <Button onClick={handleRunReport} disabled={chips.length === 0 && (datePreset === "any" || (datePreset === "custom" && customFrom == null && customTo == null))} color="brand" size="sm">
                  Run Report
                </Button>
              </Group>
            </Stack>
          </Collapse>
        </Stack>
      </Paper>

      {chips.length === 0 && (datePreset === "any" || (datePreset === "custom" && customFrom == null && customTo == null)) ? (
        <ReportEmptyState />
      ) : !submittedRequest || !data ? (
        <Text c="dimmed" size="sm" ta="center" mt="md">
          Click "Run Report" to see results.
        </Text>
      ) : (
        <Stack gap="md">
          <Group justify="space-between" align="center" wrap="wrap">
            <Group gap="md" align="center">
              <FoundCountBadge count={data.totalCount} singularNoun="person" pluralNoun="people" />
              <Group gap="xs" align="center">
                <Text size="sm" c="dimmed">Per page:</Text>
                <Select
                  size="xs"
                  w={70}
                  value={String(pageSize)}
                  onChange={handlePageSizeChange}
                  data={PAGE_SIZE_OPTIONS}
                  allowDeselect={false}
                />
              </Group>
            </Group>
            <ReportActionsToolbar
              onExportExcel={handleExportExcel}
              onExportCsv={handleExportCsv}
              onExportCvForSelected={handleExportCvForSelected}
              onExportCvForAll={handleExportCvForAll}
              selectedCount={selection.selectedIds.size}
              totalCount={data.totalCount}
              exporting={isExporting}
            />
          </Group>
          <ReportResultsTable
            rows={rows}
            loading={isFetching}
            selectedIds={selection.selectedIds}
            onToggleSelect={selection.handleToggleSelect}
            onSelectAllOnPage={() => selection.handleSelectAllOnPage(pagePersonIds)}
            onDeselectAllOnPage={() => selection.handleDeselectAllOnPage(pagePersonIds)}
            allSelectedOnPage={selection.allOnPageSelected(selection.selectedIds, pagePersonIds)}
            page={page}
            totalPages={data.totalPages}
            onPageChange={handlePageChange}
            matchColumnLabel="Certificate"
          />
        </Stack>
      )}

      <ReportCvExportModal
        mode={exportCvMode}
        onClose={() => setExportCvMode(null)}
        selectedIds={selection.selectedIds}
        totalCount={data?.totalCount ?? 0}
        getMatchingPersonIds={() => certificateReportService.getMatchingPersonIds(buildFilter())}
        fileNamePrefix="CVs_CertificatesReport"
        onSelectedIdsChanged={handleSelectedIdsChangedByReview}
      />
    </Stack>
  );
};
